import { useEffect, useRef } from "react";
import { Row, Col, Table, Button } from "react-bootstrap";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import useMarketerInvoice from "../../hooks/useMarketerInvoice";
import { DownloadDone } from "@mui/icons-material";
import { Download } from "react-bootstrap-icons";

const MarketerInvoice = () => {
  const invoiceRef = useRef();
  const navigate = useNavigate();
  const shopid = localStorage.getItem("shopid");

  const {
    invoiceData: userdata,
    isLoading,
    isError,
    error,
    refetch,
  } = useMarketerInvoice(shopid);

  /* ===============================
     DOWNLOAD PDF
  =============================== */
  const downloadPDF = () => {
    html2canvas(invoiceRef.current).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

      pdf.save("invoice.pdf");
      navigate(-1);
    });
  };

  useEffect(() => {
    if (!shopid) return;
    refetch();
  }, [shopid, refetch]);

  const paymentStatus = userdata?.paymentStatus || "pending";
  const isPending = paymentStatus === "pending";
  const isApproved = paymentStatus === "success";

  const statusLabel = isApproved
    ? "Approved"
    : isPending
      ? "Pending"
      : "Failed";

  const statusVariant = isApproved
    ? "success"
    : isPending
      ? "warning"
      : "danger";

  const canDownload = isApproved && !!userdata;

  if (isLoading) {
    return (
      <Row className="screenHeight d-flex justify-content-center align-items-center">
        <Col lg={7} sm={10} xs={11}>
          <div className="text-center">Loading invoice...</div>
        </Col>
      </Row>
    );
  }

  if (isError) {
    return (
      <Row className="screenHeight d-flex justify-content-center align-items-center">
        <Col lg={7} sm={10} xs={11}>
          <div className="text-center text-danger">
            {error?.data?.message || "Unable to load invoice."}
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row ref={invoiceRef}>
      <div className="screenHeight d-flex justify-content-center align-items-center">
        <Col lg={7} sm={10} xs={11}>
          {/* HEADER */}
          <Row className="justify-content-between align-items-center mb-4">
            <Col>
              <h4>
                <strong>SELECTEZI</strong>
              </h4>
              
            </Col>

            <Col className="text-end g-2">
            <span className={`badge me-3 bg-${statusVariant} mt-2`}>{statusLabel}</span>
              <Button
                variant="primary"
                onClick={downloadPDF}
                disabled={!canDownload}
              >
                <Download /> &nbsp;
                Download PDF
              </Button>
            </Col>
          </Row>

          {/* INVOICE INFO */}
          <Row className="mb-4">
            <Col md={6}>
              <h2>Invoice</h2>
              <p>
                <strong>
                  {userdata?.invoiceNo || "########"}
                </strong>
              </p>
            </Col>

            <Col md={6} className="border p-3 rounded">
              <p>
                <strong>Issue Date:</strong>{" "}
                {moment(userdata?.createdAt).format("DD-MM-YYYY")}
              </p>

              <p>
                <strong>Amount Due:</strong>{" "}
                {userdata?.planId?.pricing?.monthly?.currency}{" "}
                {userdata?.planId?.pricing?.monthly?.amount}
              </p>
            </Col>
          </Row>

          {/* FROM / TO */}
          <Row className="mb-4 border p-3 rounded">
            <Col md={6}>
              <h5>From</h5>
              <p>
                Selectezi Pvt Ltd.
                <br />
                123 Business Avenue, Suite 456
                <br />
                New York, NY 10001
                <br />
                accounts@invoicehub.com
                <br />
                +1 (555) 123-4567
              </p>
            </Col>

            <Col md={6}>
              <h5>To</h5>
              <p>
                {userdata?.userid?.fullname}
                <br />
                {userdata?.shopid?.shopname}
                <br />
                {userdata?.shopid?.shopaddress}
                <br />
                {userdata?.userid?.country},{" "}
                {userdata?.userid?.state},{" "}
                {userdata?.userid?.city}
                <br />
                {userdata?.userid?.email}
                <br />
                {userdata?.userid?.phonenumber}
              </p>
            </Col>
          </Row>

          {/* SUBSCRIPTION DETAILS */}
          <Row className="mb-4 border p-3 rounded">
            <h5>Subscription Details</h5>

            <Table bordered responsive>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Description</th>
                  <th>Duration</th>
                  <th>Amount</th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>
                    <strong>
                      {userdata?.planId?.planName}
                    </strong>
                  </td>

                  <td>
                    {userdata?.planId?.features
                      ?.map((f) => f.title)
                      .join(", ")}
                  </td>

                  <td>
                    {userdata?.planId?.isDefault
                      ? "Free Plan"
                      : "Subscription"}
                  </td>

                  <td>
                    {userdata?.planId?.pricing?.monthly?.currency}{" "}
                    {userdata?.planId?.pricing?.monthly?.amount}
                  </td>
                </tr>

                <tr>
                  <td colSpan="2"></td>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    {userdata?.planId?.pricing?.monthly?.currency}{" "}
                    {userdata?.planId?.pricing?.monthly?.amount}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Row>

          {/* PAYMENT INFO */}
          <Row className="mb-4 border p-3 rounded">
            <h5>Payment Information</h5>

            <p>
              <strong>Bank Name:</strong> First National Bank
              <br />
              <strong>Account Holder:</strong> Selectezi Pvt Ltd.
              <br />
              <strong>IBAN:</strong> US29 FNBK 5678 7654 3210 9876
              <br />
              <strong>SWIFT/BIC:</strong> FNBKUS33XXX
              <br />
              <strong>Bank Address:</strong> 456 Banking Street,
              New York, NY 10002
            </p>
          </Row>

          {/* PAYMENT INSTRUCTIONS */}
          <Row className="mb-4 border p-3 rounded">
            <h5>Payment Instructions</h5>

            <p>
              Please make payment by the due date. After completing
              the payment, send the payment confirmation to the email
              address below.
            </p>
          </Row>

          {/* FOOTER */}
          <footer className="text-center mt-4 text-muted">
            Selectezi Pvt Ltd. | www.selectezi.com |
            accounts@invoicehub.com | +1 (555) 123-4567
          </footer>
        </Col>
      </div>
    </Row>
  );
};

export default MarketerInvoice;
